import { useCallback, useRef, useState } from 'react'
import api from '../../../helpers/apiRequester'
import type { ViewThruBatchAnalysis } from '../viewThruBatchView/BatchAnalysis'
import type { CcjRule } from './ccjRules'
import type { ImportedMatchResponse } from './importedReport'

export type ImportedReportState =
    | { status: 'idle' }
    | { status: 'running'; batchId: string }
    | {
          status: 'complete'
          batchId: string
          ccjRule: CcjRule
          matches: ImportedMatchResponse
          /** Null when the analysis failed; the SRDS match still stands. */
          analysis: ViewThruBatchAnalysis | null
          analysisError: string | null
          ranAt: Date
      }
    | { status: 'failed'; batchId: string; error: string }

const errorText = (error: any, fallback: string) => error?.response?.data?.message || error?.message || fallback

/**
 * Analyses one batch and, at the same time, matches every client in it
 * against Sentinel Reporting. The match is the report; the analysis adds the
 * eligibility verdict next to each name, so a failed analysis is reported but
 * does not stop the match from showing.
 */
export const useImportedReport = () => {
    const [state, setState] = useState<ImportedReportState>({ status: 'idle' })
    const runIdRef = useRef(0)

    const run = useCallback(async (batchId: string, ccjRule: CcjRule) => {
        if (!batchId) return
        const runId = ++runIdRef.current
        setState({ status: 'running', batchId })

        const matchRequest = api
            .post('viewthru-batch-imported', { batchId })
            .then((response) => {
                if (!response.data?.success) {
                    throw new Error(response.data?.message || 'Could not match the batch against Sentinel Reporting.')
                }
                return response.data as ImportedMatchResponse
            })
        const analysisRequest = api({ url: 'viewthru-batch-analysis', data: { batchId, ccjRule } })
            .then((response) => {
                if (!response.data?.success) throw new Error(response.data?.message || 'Could not analyse this batch.')
                return { analysis: response.data as ViewThruBatchAnalysis, error: null as string | null }
            })
            .catch((error: any) => ({
                analysis: null as ViewThruBatchAnalysis | null,
                error: errorText(error, 'Could not analyse this batch.') as string | null,
            }))

        try {
            const [matches, analysed] = await Promise.all([matchRequest, analysisRequest])
            if (runIdRef.current !== runId) return
            setState({
                status: 'complete',
                batchId,
                ccjRule,
                matches,
                analysis: analysed.analysis,
                analysisError: analysed.error,
                ranAt: new Date(),
            })
        } catch (error: any) {
            if (runIdRef.current !== runId) return
            setState({
                status: 'failed',
                batchId,
                error: errorText(error, 'Could not match the batch against Sentinel Reporting.'),
            })
        }
    }, [])

    const reset = useCallback(() => {
        runIdRef.current += 1
        setState({ status: 'idle' })
    }, [])

    return { state, run, reset, running: state.status === 'running' }
}
